import {
    Accordion,
    Code,
    Group,
    Stack,
    Text,
} from '@mantine/core';

import {StepStatusBadge} from './StepStatusBadge.tsx';
import {ExecutionStep} from '../types.ts';
import {formatTimestamp} from '../../../utils/TableTimestamps.tsx';


// Extracted from executions/pages/EditPage.tsx during FNP-79 so the
// applications detail page can render the same step UI when an Application
// has a workflowId.
const formatTimeRange = (startedAt?: string, completedAt?: string) => {
    if (!startedAt && !completedAt) {
        return 'n/a';
    }
    const start = startedAt ? formatTimestamp(startedAt) : 'pending';
    const end = completedAt ? formatTimestamp(completedAt) : 'in progress';
    return `${start} → ${end}`;
};


export const StepBlock = ({step}: { step: ExecutionStep }) => {
    return (
        <Stack gap={'xs'}>
            <Group justify={'space-between'} wrap={'nowrap'}>
                <Group gap={'xs'} wrap={'nowrap'}>
                    <Text fw={600} size={'sm'}>{step.position}. {step.stepName ?? step.taskStateName ?? step.id}</Text>
                    <StepStatusBadge status={step.stepStatus}/>
                </Group>
                <Text c={'dimmed'} size={'xs'}>
                    {formatTimeRange(step.startedAt, step.completedAt)}
                    {typeof step.elapsedMs === 'number' ? ` · ${step.elapsedMs} ms` : ''}
                    {typeof step.retryCount === 'number' && step.retryCount > 0 ? ` · ${step.retryCount} retries` : ''}
                </Text>
            </Group>

            {step.errorMessage || step.errorType ? (
                <Stack gap={4} pl={'md'}>
                    {step.errorType ? (
                        <Text size={'xs'} c={'red'}>
                            <Text span fw={600}>Error type:</Text> {step.errorType}
                        </Text>
                    ) : null}
                    {step.errorMessage ? (
                        <Text size={'xs'} c={'red'}>
                            <Text span fw={600}>Error:</Text> {step.errorMessage}
                        </Text>
                    ) : null}
                </Stack>
            ) : null}

            {step.input ? (
                <Accordion variant={'separated'} chevronPosition={'left'}>
                    <Accordion.Item value={'input'}>
                        <Accordion.Control>
                            <Text size={'xs'} fw={600}>Input</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Code block style={{whiteSpace: 'pre-wrap'}}>{step.input}</Code>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            ) : null}

            {step.output ? (
                <Accordion variant={'separated'} chevronPosition={'left'}>
                    <Accordion.Item value={'output'}>
                        <Accordion.Control>
                            <Text size={'xs'} fw={600}>Output</Text>
                        </Accordion.Control>
                        <Accordion.Panel>
                            <Code block style={{whiteSpace: 'pre-wrap'}}>{step.output}</Code>
                        </Accordion.Panel>
                    </Accordion.Item>
                </Accordion>
            ) : null}
        </Stack>
    );
};
